import { Box, Center, Flex, Grid, Icon, Progress, Spinner, Text, useToast } from '@chakra-ui/react'
import { HeadingPages } from '../../../components/HeadingPages'
import { Input } from '../../../components/Form'
import { SelectComponent } from '../../../components/Form/Select'
import * as T from '../../../components/Table'
import { useForm } from 'react-hook-form'
import { Button } from '../../../components/Button'
import { FaFileExcel } from 'react-icons/fa'
import { api } from '../../../services/api'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Pagination } from '../../../components/Pagination'
import React, { useEffect } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDispatch, useSelector } from 'react-redux'
import { pauseQueueFetch, startQueueFetch } from '../../../redux/features/queues/fetchStatusQueuesSlice'
import { ArtigoItem } from './ArtigoItem'

const searchSchema = z.object({
  title: z.string().optional(),
  keyword: z.string().optional(),
  category: z
    .object({
      label: z.string().optional(),
      value: z.string().optional(),
    })
    .optional(),
  initialDate: z.string().optional(),
  finalDate: z.string().optional(),
  publicado: z
    .object({
      label: z.string().optional(),
      value: z.boolean().optional(),
    })
    .optional(),
})

async function getAllArticles({ page, limit, aprovados, ...search }) {
  const response = await api.get(`/crm-interno/listar-artigos`, {
    params: {
      page,
      limit,
      q: search.title,
      data_inicial: search.initialDate,
      data_final: search.finalDate,
      categoria: search.category?.value,
      keyword: search.keyword,
      aprovados: search.publicado.value || false,
    },
  })
  return response.data
}

async function getStatusArticles({ queue }) {
  const response = await api.get(`/crm-interno/status-artigos`, {
    params: {
      queue,
    },
  })
  return response.data
}

async function getCategories() {
  const response = await api.get('/crm-interno/listar-categorias-artigos')

  return response.data
}

async function uploadPlanilha({ planilha }) {
  const formData = new FormData()
  formData.append('planilha', planilha)

  const response = await api.post('/crm-interno/criar-artigo-planilha', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

export function Artigos() {
  const { control, handleSubmit, register, formState, setValue } = useForm({
    resolver: zodResolver(searchSchema),
  })
  const queryClient = useQueryClient()
  const toast = useToast({
    position: 'top-right',
    duration: 10000,
    isClosable: true,
  })

  console.log(formState?.errors)

  const dispatch = useDispatch()

  const fetchStatusQueue = useSelector((state) => state.fetchStatusQueue.fetchQueueStatus)

  const [currentPage, setCurrentPage] = React.useState(1)

  const [search, setSearch] = React.useState({
    title: '',
    keyword: '',
    category: null,
    initialDate: '',
    finalDate: '',
    publicado: false,
  })

  const { data: status } = useQuery({
    queryKey: ['status'],
    queryFn: () => getStatusArticles({ queue: 'artigos' }),
    enabled: !!fetchStatusQueue,
    refetchInterval: 5000,
    onSuccess: (data) => {
      queryClient.invalidateQueries('articles')

      if (data.em_espera?.quantidade === 0 && data.processando?.quantidade === 0) {
        dispatch(pauseQueueFetch())

        toast({
          title: 'Processamento finalizado',
          description: 'Todos os artigos foram processados',
          status: 'success',
        })
      }
    },
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['articles', { page: currentPage, limit: 30 }],
    queryFn: () => getAllArticles({ page: currentPage, limit: 20, ...search }),
    keepPreviousData: true,
    staleTime: 5000,
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 5000,
    refetchOnWindowFocus: false,
  })

  const { mutateAsync: uploadPlanilhaAsync, isLoading: isLoadingUploadPlanilha } = useMutation({
    mutationKey: 'uploadPlanilha',
    mutationFn: uploadPlanilha,
    onSuccess: (data) => {
      queryClient.invalidateQueries('articles')
      toast({
        title: 'Planilha enviada com sucesso',
        // description: `Colunas processadas: ${data.colunas_processadas.map((item) => item)}`,
        status: 'success',
      })

      dispatch(startQueueFetch())
    },
  })

  async function handleUploadPlanilha(event) {
    if (event.target.files.length === 0) {
      return
    }

    console.log(event.target.files.item(0))

    await uploadPlanilhaAsync({ planilha: event.target.files.item(0) })

    //limpa o input file
    event.target.value = ''
  }

  async function handleSearch(data) {
    console.log(data)
    setSearch(data)

    await new Promise((resolve) => setTimeout(resolve, 1000))

    refetch()
  }

  useEffect(() => {
    setValue('publicado', { label: 'Não', value: false })
    setValue('category', { label: 'Selecione...', value: '' })
  }, [setValue])

  return (
    <>
      <HeadingPages name="Artigos" />
      {isLoadingUploadPlanilha && (
        <Center position="fixed" inset="0" bgColor="rgba(0, 0, 0, 0.5)" zIndex="9999" color="white" fontSize="2xl" fontWeight="bold">
          <Text>Enviando planilha...</Text>
          <Spinner size="lg" ml="2" />
        </Center>
      )}

      <Flex flexDir="column" gap="20px" mt="20px" maxW="1280px" px="12px" mx="auto" mb="10">
        <Grid as="form" gridTemplateColumns={'666px 1fr'} gap={'12px'} onSubmit={handleSubmit(handleSearch)}>
          <Input label="Pesquisar por título" {...register('title')} />
          <Flex gap="12px">
            <Input label="Keyword" {...register('keyword')} />
            <SelectComponent
              name="publicado"
              label="Publicado"
              control={control}
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
            />
          </Flex>
          <SelectComponent
            name="category"
            label="Categoria"
            control={control}
            options={categories?.map((item) => ({
              label: item.nome_categoria,
              value: item.id_categoria,
            }))}
          />
          <Flex gap="5px" alignItems="flex-end">
            <Input label="Data Inicial" type="date" {...register('initialDate')} />
            <Input label="Data Final" type="date" {...register('finalDate')} />
            <Button size="sm" type="submit" px="30px" isLoading={formState.isSubmitting} isDisabled={!formState.isDirty}>
              Buscar
            </Button>
          </Flex>
        </Grid>

        <Flex alignItems="center" justifyContent="space-between" gap={10}>
          <Button
            as="label"
            htmlFor="planilha"
            cursor="pointer"
            bgColor="green.500"
            size="sm"
            type="submit"
            px="30px"
            mr="auto"
            leftIcon={<Icon color="white" as={FaFileExcel} fontSize="lg" />}
          >
            Upload de planilha de palavras chave
            <input onChange={handleUploadPlanilha} accept=".xls,.xlsx,.csv" id="planilha" type="file" style={{ display: 'none' }} />
          </Button>
          {data && (
            <Flex gap={2} alignItems="center" p={1} rounded="md" bg="gray.200" border="1px solid" borderColor="gray.300" boxShadow="sm">
              <Text color="red.500" whiteSpace="nowrap" fontSize="sm" fontWeight="light">
                <strong>{data.total_nao_aprovado}</strong> artigo(s) não aprovados
              </Text>
              <Box w="1px" h="20px" bgColor="gray.300" />
              <Text color="green.500" whiteSpace="nowrap" fontSize="sm" fontWeight="light">
                <strong>{data?.total_aprovado}</strong> artigo(s) aprovados
              </Text>
              <Box w="1px" h="20px" bgColor="gray.300" />
              <Text color="blue.500" whiteSpace="nowrap" fontSize="sm" fontWeight="light">
                <strong>{data.total_aprovado_e_nao_aprovado}</strong> total
              </Text>
            </Flex>
          )}
          {fetchStatusQueue && (
            <Flex alignItems="center" gap={2}>
              <Flex alignItems="center" gap={2} fontSize="sm">
                <Text whiteSpace="nowrap" color="yellow.500">
                  <strong>{status?.em_espera?.quantidade}</strong> em espera
                </Text>{' '}
                |
                <Text whiteSpace="nowrap" color="green.500">
                  <strong>{status?.processando?.quantidade}</strong> processando
                </Text>
                {/* |
                <Text whiteSpace="nowrap" color="blue.500">
                  <strong>{status?.falhados?.quantidade}</strong> falhados
                </Text> */}
              </Flex>
              <Progress isAnimated hasStripe value={100} w="200px" />
            </Flex>
          )}
        </Flex>
        <T.Table>
          <T.Thead>
            <T.Tr>
              <T.Th textAlign="start" px={2}>
                Nome do Artigo
              </T.Th>
              <T.Th>Data de publicação</T.Th>
              <T.Th>Categoria</T.Th>
              <T.Th>Palavra chave</T.Th>
              <T.Th>Data aprovação</T.Th>
              <T.Th />
              <T.Th />
            </T.Tr>
          </T.Thead>
          <T.Tbody>
            {isLoading && <Text>Carregando...</Text>}
            {error && <Text>Erro ao carregar os dados</Text>}

            {data?.data.map((article) => (
              <ArtigoItem key={article.artigo_id} article={article} />
            ))}
          </T.Tbody>
        </T.Table>
        <Pagination page={currentPage} setPage={setCurrentPage} limit={30} totalPages={data?.last_page} />
      </Flex>
    </>
  )
}
